/**
 * PRO-4 — the 5-step product wizard. `/products/new` and `/products/:id/edit`.
 *
 *   1. Basics            — name, category, description, composition, ТНВЭД, country, capacity
 *   2. Packaging chain   — with the live "1 фура = N палет = N упаковок = N штук" summary
 *   3. Price             — value + unit picker, live "≈ $X.XXXXXX за штуку" preview
 *   4. Media             — 6 photo slots + optional video (see MediaStep.tsx)
 *   5. Review & activate
 *
 * TWO THINGS THIS SCREEN MUST NEVER GET WRONG:
 *
 * • THE PRICE NOTICE (step 3). "MOST устанавливает финальную цену каталога; покупатели вашу цену
 *   не видят." This is not boilerplate — it is the sentence that makes a producer willing to type
 *   their FCA price into someone else's system. It appears next to the price field, always.
 *
 * • THE MARGIN GUARD (edit mode). Changing the base price or packaging of an already-published
 *   product sends it back to MOST for re-pricing, and if the new price lands at or above the
 *   current catalog price the product is HIDDEN until Merdan re-prices it (docs/04 §1.2). We warn
 *   before saving — but we phrase it as a re-pricing/visibility consequence, NEVER in terms of
 *   "margin", and we never reveal the selling price. We do not have it, and this app must not
 *   want it.
 *
 * DRAFT AUTOSAVE: the wizard creates the draft on leaving step 3 — the API requires packaging and
 * price on create, and media uploads (step 4) need a real product id — and PUTs on each
 * subsequent step transition. "The wizard must never lose data on a
 * failed step" — so a failed autosave shows an error and KEEPS the user's input; it does not
 * roll back, and it does not block them from continuing to type.
 */
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, useFormikContext } from 'formik';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Info, Lock } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  ErrorState,
  FormErrorPanel,
  FormSkeleton,
  NumberField,
  SelectField,
  TextAreaField,
  TextField,
  cn,
  useToast,
} from '@/components/ui';
import { UnsavedGuard } from '@/components/UnsavedGuard';
import { MediaStep } from './MediaStep';
import {
  EMPTY_WIZARD,
  STEP_FIELDS,
  canActivate,
  fromProduct,
  toProductInput,
  triggersReprice,
  wizardSchema,
  type WizardValues,
} from './schema';
import * as api from '@/shared/api';
import type { Category } from '@/shared/contract';
import { qk } from '@/shared/queryClient';
import { formatInt, formatPerPiece, normalizeToPerPiece, summarizeChain } from '@/shared/money';
import type { CapacityUnit, Media, PriceUnit } from '@/shared/contract';

const STEP_KEYS = ['wizard.step1', 'wizard.step2', 'wizard.step3', 'wizard.step4', 'wizard.step5'];

export function ProductWizardScreen() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [step, setStep] = React.useState(0);
  // The id of the draft once it exists — either from the URL (edit) or created on step 3 → 4.
  const [productId, setProductId] = React.useState<string | undefined>(id);
  const [showReprice, setShowReprice] = React.useState(false);
  const [activated, setActivated] = React.useState(false);

  const categories = useQuery({ queryKey: qk.categories, queryFn: api.listCategories });

  const product = useQuery({
    queryKey: qk.product(productId ?? ''),
    queryFn: () => api.getProduct(productId!),
    enabled: Boolean(productId),
  });

  const media: Media[] = product.data?.media ?? [];
  const photoCount = media.filter((m) => m.kind === 'photo').length;

  // An already-active, PRICED product is the one the margin guard can bite. A draft, or an
  // active-but-unpriced product, has no selling price to protect — editing it is unremarkable.
  const isPublished = product.data?.status === 'active' && product.data.is_priced === true;

  const initialValues: WizardValues = React.useMemo(
    () => (product.data ? fromProduct(product.data) : EMPTY_WIZARD),
    [product.data],
  );

  // The values as last persisted — the baseline for "did this edit touch a re-pricing field?".
  const savedRef = React.useRef<WizardValues>(initialValues);
  React.useEffect(() => {
    savedRef.current = initialValues;
  }, [initialValues]);

  const save = useMutation({
    mutationFn: async (values: WizardValues) => {
      const input = toProductInput(values);
      if (productId) return api.updateProduct(productId, input);
      return api.createProduct(input);
    },
    onSuccess: (saved) => {
      setProductId(saved.id);
      savedRef.current = fromProduct(saved);
      qc.setQueryData(qk.product(saved.id), saved);
      void qc.invalidateQueries({ queryKey: ['producer', 'products'] });
    },
  });

  const activate = useMutation({
    mutationFn: () => api.activateProduct(productId!),
    onSuccess: (saved) => {
      qc.setQueryData(qk.product(saved.id), saved);
      void qc.invalidateQueries({ queryKey: ['producer', 'products'] });
      setActivated(true);
    },
  });

  if (isEdit && product.isLoading) return <FormSkeleton fields={8} />;
  if (isEdit && product.isError) {
    return <ErrorState error={product.error} onRetry={() => void product.refetch()} />;
  }

  // Category names ARE translated — they come per-locale from the admin-managed columns
  // (docs/06 §8), unlike product content, which renders as the producer typed it.
  const localName = (c: Category) =>
    i18n.language.startsWith('en')
      ? c.name_en
      : i18n.language.startsWith('tk')
        ? c.name_tk
        : c.name_ru;

  // Roots first, each followed by its subcategories; a subcategory is labeled
  // "Parent / Child" so the flat dropdown still reads as a tree.
  const cats = categories.data ?? [];
  const categoryOptions = cats
    .filter((c) => c.parent_id === null)
    .flatMap((root) => [
      { value: root.id, label: localName(root) },
      ...cats
        .filter((c) => c.parent_id === root.id)
        .map((c) => ({ value: c.id, label: `${localName(root)} / ${localName(c)}` })),
    ]);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {isEdit ? t('wizard.titleEdit') : t('wizard.titleNew')}
        </h1>
      </div>

      <Stepper step={step} onStep={setStep} maxReachable={productId ? 4 : 0} />

      <Formik
        initialValues={initialValues}
        validationSchema={wizardSchema(t)}
        enableReinitialize
        // The wizard's "submit" is the ACTIVATE action on step 5; each step's Next does its own
        // partial validation + autosave, so there is nothing to do here.
        onSubmit={() => {}}
      >
        {(formik) => {
          const dirty = formik.dirty;

          /** Validate only the fields the user can actually see right now. */
          const validateStep = async (): Promise<boolean> => {
            const fields = STEP_FIELDS[step] ?? [];
            if (fields.length === 0) return true;

            const errors = await formik.validateForm();
            const stepErrors = fields.filter((f) => errors[f]);

            if (stepErrors.length > 0) {
              // Touch them so the errors actually render (they are gated on `touched`).
              const touched: Record<string, boolean> = {};
              for (const f of fields) touched[f] = true;
              void formik.setTouched({ ...formik.touched, ...touched });
              return false;
            }
            return true;
          };

          /** Persist the draft, then advance. Never advances on a failed save. */
          const persistAndGo = async (nextStep: number) => {
            try {
              await save.mutateAsync(formik.values);
              formik.resetForm({ values: formik.values });
              setStep(nextStep);
            } catch {
              // The mutation's error is rendered by the panel below. The user's input is intact —
              // "the wizard never loses data on a failed step".
              toast(t('wizard.draftFailed'), 'error');
            }
          };

          const onNext = async () => {
            if (!(await validateStep())) return;

            // Steps 1–3 own persisted fields; 4 (media) uploads directly; 5 is review-only.
            // POST /producer/products demands the packaging chain AND the price (docs/05, NOT NULL
            // columns), so a NEW product is only created on leaving step 3. Once it exists, every
            // dirty step transition PUTs the full (already-valid) values.
            const needsSave = step <= 2 && (productId ? dirty : step === 2);

            if (!needsSave) {
              setStep(step + 1);
              return;
            }

            // MARGIN GUARD: warn BEFORE writing, if this edit touches a re-pricing field on a
            // product that is currently live in the catalog.
            if (isPublished && triggersReprice(savedRef.current, formik.values)) {
              setShowReprice(true);
              return;
            }

            await persistAndGo(step + 1);
          };

          const confirmReprice = async () => {
            setShowReprice(false);
            await persistAndGo(step + 1);
          };

          return (
            <Form className="space-y-6">
              {/* Don't nag on the very last screen after a successful activation. */}
              <UnsavedGuard when={dirty && !activated && !save.isPending} />

              <Card>
                <CardContent className="space-y-5 p-6">
                  {step === 0 ? (
                    <BasicsStep
                      categoryOptions={categoryOptions}
                      loading={categories.isLoading}
                      categoriesFailed={categories.isError}
                      onRetryCategories={() => void categories.refetch()}
                    />
                  ) : null}
                  {step === 1 ? <PackagingStep /> : null}
                  {step === 2 ? <PriceStep /> : null}
                  {step === 3 ? (
                    productId ? (
                      <MediaStep productId={productId} media={media} />
                    ) : null
                  ) : null}
                  {step === 4 ? (
                    <ReviewStep
                      photoCount={photoCount}
                      categoryOptions={categoryOptions}
                      activated={activated}
                    />
                  ) : null}
                </CardContent>
              </Card>

              {save.isError ? <FormErrorPanel error={save.error} /> : null}
              {activate.isError ? <FormErrorPanel error={activate.error} /> : null}

              {/* ── Footer ── */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(Math.max(0, step - 1))}
                  disabled={step === 0}
                >
                  {t('common.back')}
                </Button>

                <div className="flex items-center gap-3">
                  {save.isPending ? (
                    <span className="text-xs text-muted-foreground" aria-live="polite">
                      {t('wizard.draftSaving')}
                    </span>
                  ) : productId && !dirty && step > 0 ? (
                    <span className="text-xs text-emerald-600" aria-live="polite">
                      {t('wizard.draftSaved')}
                    </span>
                  ) : null}

                  {step < 4 ? (
                    <Button type="button" onClick={() => void onNext()} loading={save.isPending}>
                      {t('common.next')}
                    </Button>
                  ) : (
                    <ActivateButton
                      photoCount={photoCount}
                      activated={activated}
                      pending={activate.isPending || save.isPending}
                      dirty={dirty}
                      onSaveFirst={() => void persistAndGo(4)}
                      onActivate={() => activate.mutate()}
                      onDone={() => navigate('/products')}
                    />
                  )}
                </div>
              </div>

              {/* THE MARGIN-GUARD WARNING. Phrased as a re-pricing + visibility consequence.
                  No mention of margin. No selling price — we do not have it. */}
              <ConfirmDialog
                open={showReprice}
                onClose={() => setShowReprice(false)}
                onConfirm={() => void confirmReprice()}
                loading={save.isPending}
                title={t('wizard.repriceWarnTitle')}
                description={
                  <div className="space-y-2">
                    <p>{t('wizard.repriceWarnBody')}</p>
                    <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                      {t('wizard.repriceWarnHidden')}
                    </p>
                  </div>
                }
                confirmLabel={t('wizard.repriceWarnConfirm')}
              />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

// ── Stepper ─────────────────────────────────────────────────────────────────────────────────
function Stepper({
  step,
  onStep,
  maxReachable,
}: {
  step: number;
  onStep: (s: number) => void;
  /** Steps beyond this are unreachable by click — media needs a saved draft to attach to. */
  maxReachable: number;
}) {
  const { t } = useTranslation();

  return (
    <ol className="flex flex-wrap gap-1 rounded-lg border border-border bg-card p-1">
      {STEP_KEYS.map((key, i) => {
        const active = i === step;
        const done = i < step;
        const reachable = i <= Math.max(step, maxReachable);

        return (
          <li key={key} className="flex-1">
            <button
              type="button"
              disabled={!reachable}
              onClick={() => reachable && onStep(i)}
              aria-current={active ? 'step' : undefined}
              className={cn(
                'flex w-full items-center justify-center gap-2 rounded-md px-2 py-2 text-xs font-medium transition-colors',
                active
                  ? 'bg-foreground text-white'
                  : done
                    ? 'text-emerald-700 hover:bg-emerald-50'
                    : reachable
                      ? 'text-muted-foreground hover:bg-muted/60'
                      : 'cursor-not-allowed text-muted-foreground/50',
              )}
            >
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px]',
                  active
                    ? 'bg-card/20'
                    : done
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-muted text-muted-foreground',
                )}
              >
                {done ? <Check className="h-3 w-3" aria-hidden /> : i + 1}
              </span>
              <span className="hidden truncate sm:inline">{t(key)}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// ── Step 1: basics ──────────────────────────────────────────────────────────────────────────
function BasicsStep({
  categoryOptions,
  loading,
  categoriesFailed,
  onRetryCategories,
}: {
  categoryOptions: Array<{ value: string; label: string }>;
  loading: boolean;
  categoriesFailed: boolean;
  onRetryCategories: () => void;
}) {
  const { t } = useTranslation();

  const capacityUnits: Array<{ value: CapacityUnit; label: string }> = [
    { value: 'piece', label: t('units.piece') },
    { value: 'pack', label: t('units.pack') },
    { value: 'box', label: t('units.box') },
    { value: 'pallet', label: t('units.pallet') },
    { value: 'truck', label: t('units.truck') },
  ];

  return (
    <>
      <TextField name="name" label={t('wizard.name')} hint={t('wizard.nameHint')} required />

      <div className="grid gap-5 md:grid-cols-2">
        <SelectField
          name="category_id"
          label={t('wizard.category')}
          placeholder={loading ? t('common.loading') : t('wizard.categoryPlaceholder')}
          options={categoryOptions}
          // An empty option list makes the trigger inert by design (nothing to open). Without
          // saying WHY, that is indistinguishable from a broken control — so a failed fetch
          // states itself and offers the retry.
          hint={
            categoriesFailed ? (
              <span className="text-destructive">
                {t('wizard.categoryLoadFailed')}{' '}
                <button
                  type="button"
                  onClick={onRetryCategories}
                  className="underline underline-offset-2"
                >
                  {t('common.retry')}
                </button>
              </span>
            ) : undefined
          }
          required
        />
        <TextField
          name="country_of_origin"
          label={t('wizard.countryOfOrigin')}
          maxLength={2}
          required
        />
      </div>

      <TextAreaField
        name="description"
        label={t('wizard.description')}
        hint={t('wizard.descriptionHint')}
        rows={3}
      />
      <TextAreaField name="composition" label={t('wizard.composition')} rows={2} />

      <div className="grid gap-5 md:grid-cols-3">
        <TextField
          name="tnved_code"
          label={t('wizard.tnved')}
          hint={t('wizard.tnvedHint')}
          required
        />
        <NumberField
          name="capacity_value"
          label={t('wizard.capacityValue')}
          decimal
        />
        <SelectField
          name="capacity_unit"
          label={t('wizard.capacityUnit')}
          placeholder="—"
          options={capacityUnits}
        />
      </div>
    </>
  );
}

// ── Step 2: packaging chain, with the live summary line ─────────────────────────────────────
function PackagingStep() {
  const { t } = useTranslation();
  const { values } = useFormikContext<WizardValues>();

  const summary = summarizeChain(values);

  return (
    <>
      <div>
        <h2 className="text-sm font-medium text-foreground">{t('wizard.packagingTitle')}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('wizard.packagingHint')}</p>
      </div>

      <NumberField
        name="unit_weight_kg"
        label={t('wizard.unitWeight')}
        hint={t('wizard.unitWeightHint')}
        suffix={t('units.kg')}
        decimal
        required
        className="md:max-w-xs"
      />

      {/* Each field annotated with its Russian domain term (docs/06 §5.3). */}
      <div className="grid gap-5 md:grid-cols-2">
        <NumberField
          name="pieces_per_pack"
          label={t('wizard.piecesPerPack')}
          suffix={t('units.piece_plural')}
          required
        />
        <NumberField
          name="packs_per_box"
          label={t('wizard.packsPerBox')}
          suffix={t('units.pack_plural')}
        />
        <NumberField
          name="boxes_per_pallet"
          label={t('wizard.boxesPerPallet')}
          suffix={t('units.box_plural')}
          required
        />
        <NumberField
          name="pallets_per_truck"
          label={t('wizard.palletsPerTruck')}
          hint={t('wizard.palletsPerTruckHint')}
          suffix={t('units.pallet_plural')}
        />
      </div>

      {/* The live summary. Recomputed on every keystroke, announced politely so it is not just a
          visual affordance. */}
      <div
        className="rounded-md border border-border bg-muted/60 px-4 py-3 text-sm"
        aria-live="polite"
      >
        {summary ? (
          <p className="font-medium text-foreground">
            {t('wizard.chainSummary', {
              pallets: formatInt(summary.pallets),
              boxes: formatInt(summary.boxes),
              packs: formatInt(summary.packs),
              pieces: formatInt(summary.pieces),
            })}
          </p>
        ) : (
          <p className="text-muted-foreground">{t('wizard.chainIncomplete')}</p>
        )}
      </div>
    </>
  );
}

// ── Step 3: price ───────────────────────────────────────────────────────────────────────────
function PriceStep() {
  const { t } = useTranslation();
  const { values } = useFormikContext<WizardValues>();

  // The unit picker offers piece/pack/box/pallet. NEVER truck — openapi `PriceUnit` excludes it.
  const priceUnits: Array<{ value: PriceUnit; label: string }> = [
    { value: 'piece', label: t('units.piece') },
    { value: 'pack', label: t('units.pack') },
    { value: 'box', label: t('units.box') },
    { value: 'pallet', label: t('units.pallet') },
  ];

  // Live normalization preview: value ÷ pieces_in(unit), 6 dp (docs/04 §2.2). This is the ONLY
  // price math this app does, and it operates purely on the producer's OWN price.
  const perPiece = normalizeToPerPiece(
    values.producer_price_value,
    values.producer_price_unit || undefined,
    values,
  );

  return (
    <>
      <h2 className="text-sm font-medium text-foreground">{t('wizard.priceTitle')}</h2>

      <div className="grid gap-5 md:grid-cols-2">
        <NumberField
          name="producer_price_value"
          label={t('wizard.priceValue')}
          suffix="USD"
          decimal
          required
        />
        <SelectField
          name="producer_price_unit"
          label={t('wizard.priceUnit')}
          placeholder="—"
          options={priceUnits}
          required
        />
      </div>

      <div
        className="rounded-md border border-border bg-muted/60 px-4 py-3"
        aria-live="polite"
      >
        {perPiece ? (
          <>
            <p className="text-sm font-medium tabular-nums text-foreground">
              {t('wizard.pricePreview', { price: formatPerPiece(perPiece) })}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t('wizard.pricePreviewHint')}</p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t('wizard.priceIncomplete')}</p>
        )}
      </div>

      {/* THE NOTICE. The sentence that makes the whole intermediary model acceptable to a
          producer. It is not decoration and it does not move. */}
      <div className="flex items-start gap-3 rounded-md border border-primary/50 bg-primary-muted px-4 py-3">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-link" aria-hidden />
        <p className="text-sm text-foreground">{t('wizard.priceNotice')}</p>
      </div>
    </>
  );
}

// ── Step 5: review ──────────────────────────────────────────────────────────────────────────
function ReviewStep({
  photoCount,
  categoryOptions,
  activated,
}: {
  photoCount: number;
  categoryOptions: Array<{ value: string; label: string }>;
  activated: boolean;
}) {
  const { t } = useTranslation();
  const { values } = useFormikContext<WizardValues>();

  const ready = canActivate(values, photoCount);
  const summary = summarizeChain(values);
  const perPiece = normalizeToPerPiece(
    values.producer_price_value,
    values.producer_price_unit || undefined,
    values,
  );
  const categoryLabel = categoryOptions.find((c) => c.value === values.category_id)?.label ?? '—';

  if (activated) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
          <Check className="h-6 w-6 text-emerald-700" aria-hidden />
        </div>
        <p className="text-base font-semibold text-foreground">{t('wizard.activateSuccess')}</p>
        <p className="max-w-sm text-sm text-muted-foreground">{t('wizard.activateSuccessBody')}</p>
      </div>
    );
  }

  return (
    <>
      <div>
        <h2 className="text-sm font-medium text-foreground">{t('wizard.reviewTitle')}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('wizard.reviewHint')}</p>
      </div>

      <dl className="divide-y divide-hairline text-sm">
        <Row label={t('wizard.name')} value={values.name} />
        <Row label={t('wizard.category')} value={categoryLabel} />
        <Row label={t('wizard.countryOfOrigin')} value={values.country_of_origin} />
        <Row label={t('wizard.tnved')} value={values.tnved_code || '—'} />
        <Row
          label={t('wizard.capacity')}
          value={
            values.capacity_value
              ? `${values.capacity_value} ${t(`units.${values.capacity_unit || 'piece'}`)}`
              : '—'
          }
        />
        <Row
          label={t('wizard.packagingTitle')}
          value={
            summary
              ? t('wizard.chainSummary', {
                  pallets: formatInt(summary.pallets),
                  boxes: formatInt(summary.boxes),
                  packs: formatInt(summary.packs),
                  pieces: formatInt(summary.pieces),
                })
              : '—'
          }
        />
        <Row
          label={t('wizard.priceTitle')}
          value={
            perPiece
              ? `${values.producer_price_value} USD / ${t(
                  `units.${values.producer_price_unit || 'piece'}`,
                )} · ${t('wizard.pricePreview', { price: formatPerPiece(perPiece) })}`
              : '—'
          }
        />
        <Row
          label={t('wizard.photos')}
          value={
            photoCount > 0 ? (
              <Badge tone="green">{photoCount}</Badge>
            ) : (
              <Badge tone="amber">{t('wizard.needPhoto')}</Badge>
            )
          }
        />
      </dl>

      {!ready ? (
        <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
          <p className="text-sm text-amber-900">{t('wizard.activateBlocked')}</p>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-md border border-primary/50 bg-primary-muted px-4 py-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-link" aria-hidden />
          <p className="text-sm text-foreground">{t('products.activateBody')}</p>
        </div>
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-4 py-2.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{value}</dd>
    </div>
  );
}

// ── Activate button ─────────────────────────────────────────────────────────────────────────
function ActivateButton({
  photoCount,
  activated,
  pending,
  dirty,
  onSaveFirst,
  onActivate,
  onDone,
}: {
  photoCount: number;
  activated: boolean;
  pending: boolean;
  dirty: boolean;
  onSaveFirst: () => void;
  onActivate: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { values } = useFormikContext<WizardValues>();

  if (activated) {
    return (
      <Button type="button" onClick={onDone}>
        {t('common.close')}
      </Button>
    );
  }

  // Unsaved edits must land before activation, or the server validates the OLD row.
  if (dirty) {
    return (
      <Button type="button" variant="outline" onClick={onSaveFirst} loading={pending}>
        {t('wizard.saveOnly')}
      </Button>
    );
  }

  const ready = canActivate(values, photoCount);

  return (
    <Button
      type="button"
      onClick={onActivate}
      loading={pending}
      // Enabled only with ≥1 photo and every required field — the same bar the server enforces.
      disabled={!ready}
      title={ready ? undefined : t('wizard.activateBlocked')}
    >
      {t('wizard.activate')}
    </Button>
  );
}
